import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getChannels, getChannelUnits, changeUnitStatus } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Home, 
  Building2,
  Search,
  Loader2,
  Lock,
  CheckCircle2,
  Clock,
  X,
  IndianRupee,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const ChannelPartnerPortal = () => {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Unit detail
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [isUnitDialogOpen, setIsUnitDialogOpen] = useState(false);
  const [isBlockDialogOpen, setIsBlockDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Block form
  const [blockForm, setBlockForm] = useState({
    reason: '',
    reason_detail: '',
    block_hours: 24
  });

  const loadChannels = async () => {
    try {
      const data = await getChannels();
      // Filter to partner type channels for CP users
      const partnerChannels = data.filter(c => c.channel_type === 'partner');
      setChannels(partnerChannels);
      
      if (partnerChannels.length > 0) {
        setSelectedChannel(partnerChannels[0]);
      }
    } catch (error) {
      console.error('Failed to load channels:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnits = async () => {
    if (!selectedChannel) return;
    
    setLoading(true);
    try {
      const data = await getChannelUnits(selectedChannel.channel_id);
      setUnits(data);
    } catch (error) {
      console.error('Failed to load units:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChannels();
  }, []);

  useEffect(() => {
    if (selectedChannel) {
      loadUnits();
    }
  }, [selectedChannel]);

  const handleBlock = async () => {
    if (!selectedUnit) return;
    
    setSubmitting(true);
    try {
      await changeUnitStatus(
        selectedUnit.unit_id,
        {
          status: 'soft_blocked',
          reason: blockForm.reason || 'Channel Partner Block',
          reason_detail: blockForm.reason_detail,
          block_hours: parseInt(blockForm.block_hours) || 24
        },
        selectedChannel?.channel_id
      );
      
      toast.success('Unit blocked successfully');
      setIsBlockDialogOpen(false);
      setIsUnitDialogOpen(false);
      loadUnits();
    } catch (error) {
      console.error('Failed to block unit:', error);
      toast.error(error.message || 'Failed to block unit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRelease = async (unit) => {
    setSubmitting(true);
    try {
      await changeUnitStatus(
        unit.unit_id,
        {
          status: 'available',
          reason: 'Released by Channel Partner'
        },
        selectedChannel?.channel_id
      );
      
      toast.success('Unit released');
      setIsUnitDialogOpen(false);
      loadUnits();
    } catch (error) {
      console.error('Failed to release unit:', error);
      toast.error(error.message || 'Failed to release unit');
    } finally {
      setSubmitting(false);
    }
  };

  const openBlockDialog = (unit) => {
    setSelectedUnit(unit);
    setBlockForm({
      reason: '',
      reason_detail: '',
      block_hours: 24
    });
    setIsBlockDialogOpen(true);
  };

  const filteredUnits = units.filter(unit => 
    unit.unit_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    unit.configuration.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const availableUnits = filteredUnits.filter(u => u.status === 'available');
  const blockedUnits = filteredUnits.filter(u => u.status === 'soft_blocked');
  const bookedUnits = filteredUnits.filter(u => u.status === 'booked');

  const canShowPrice = selectedChannel?.visibility?.show_pricing !== false;
  const canShowUnitNumber = selectedChannel?.visibility?.show_unit_numbers !== false;
  const canBlock = selectedChannel?.visibility?.allow_soft_block !== false;

  const formatPrice = (price) => {
    if (!canShowPrice) return '---';
    if (price >= 10000000) {
      return `₹${(price / 10000000).toFixed(2)} Cr`;
    } else if (price >= 100000) {
      return `₹${(price / 100000).toFixed(2)} L`;
    }
    return `₹${price.toLocaleString('en-IN')}`;
  };

  if (loading && channels.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (channels.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full text-center p-8">
          <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">No Access</h2>
          <p className="text-muted-foreground">
            You don't have access to any partner channels. Please contact your administrator.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="cp-portal">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-background/80 border-b border-border/40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <Building2 className="w-6 h-6 text-primary-foreground" />
              </div>
              <div>
                <h1 className="font-bold text-foreground">Partner Portal</h1>
                <p className="text-xs text-muted-foreground">Welcome, {user?.name}</p>
              </div>
            </div>
            
            {channels.length > 1 && (
              <Select 
                value={selectedChannel?.channel_id} 
                onValueChange={(v) => setSelectedChannel(channels.find(c => c.channel_id === v))}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select Channel" />
                </SelectTrigger>
                <SelectContent>
                  {channels.map(channel => (
                    <SelectItem key={channel.channel_id} value={channel.channel_id}>
                      {channel.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{availableUnits.length}</p>
            <p className="text-xs text-muted-foreground">Available</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{blockedUnits.length}</p>
            <p className="text-xs text-muted-foreground">Blocked</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-rose-600">{bookedUnits.length}</p>
            <p className="text-xs text-muted-foreground">Booked</p>
          </Card>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search units..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-cp-units"
          />
        </div>

        {/* Units Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredUnits.length === 0 ? (
          <Card className="p-12 text-center">
            <Home className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No units available</h3>
            <p className="text-muted-foreground">
              {searchQuery ? 'Try a different search term' : 'No inventory allocated to this channel'}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {filteredUnits.map(unit => (
              <div
                key={unit.unit_id}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                  unit.status === 'available' 
                    ? 'border-emerald-200 bg-emerald-50 hover:border-emerald-400 dark:border-emerald-800 dark:bg-emerald-950/30' 
                    : unit.status === 'soft_blocked'
                    ? 'border-amber-200 bg-amber-50 hover:border-amber-400 dark:border-amber-800 dark:bg-amber-950/30'
                    : 'border-rose-200 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/30 opacity-60'
                }`}
                onClick={() => { setSelectedUnit(unit); setIsUnitDialogOpen(true); }}
                data-testid={`cp-unit-${unit.unit_id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono font-bold">
                    {canShowUnitNumber ? unit.unit_number : `U-${unit.configuration}`}
                  </span>
                  {unit.status === 'available' ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  ) : unit.status === 'soft_blocked' ? (
                    <Clock className="w-5 h-5 text-amber-600" />
                  ) : (
                    <Lock className="w-5 h-5 text-rose-600" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{unit.configuration}</p>
                <p className="text-sm font-semibold mt-1">{formatPrice(unit.base_price)}</p>
                
                {unit.status === 'soft_blocked' && unit.block_expiry && (
                  <p className="text-xs text-amber-600 mt-2">
                    Expires {formatDistanceToNow(new Date(unit.block_expiry), { addSuffix: true })}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Unit Detail Dialog */}
      <Dialog open={isUnitDialogOpen} onOpenChange={setIsUnitDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          {selectedUnit && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Home className="w-5 h-5" />
                  {canShowUnitNumber ? selectedUnit.unit_number : `Unit - ${selectedUnit.configuration}`}
                </DialogTitle>
                <DialogDescription>
                  {selectedUnit.configuration} • {selectedUnit.area_sqft.toLocaleString()} sq ft
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge className={
                      selectedUnit.status === 'available' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : selectedUnit.status === 'soft_blocked'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-rose-100 text-rose-700'
                    }>
                      {selectedUnit.status === 'available' ? 'Available' : 
                       selectedUnit.status === 'soft_blocked' ? 'Blocked' : 'Booked'}
                    </Badge>
                  </div>
                  {canShowPrice && (
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Price</p>
                      <p className="text-lg font-bold">{formatPrice(selectedUnit.base_price)}</p>
                    </div>
                  )}
                </div>
                
                {selectedUnit.status === 'soft_blocked' && selectedUnit.block_expiry && (
                  <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-800">
                    <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        Block expires {formatDistanceToNow(new Date(selectedUnit.block_expiry), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Actions */}
                {canBlock && (
                  <div className="flex gap-2 pt-4 border-t">
                    {selectedUnit.status === 'available' && (
                      <Button 
                        className="flex-1"
                        onClick={() => openBlockDialog(selectedUnit)}
                        data-testid="cp-block-btn"
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        Block Unit
                      </Button>
                    )}
                    
                    {selectedUnit.status === 'soft_blocked' && (
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => handleRelease(selectedUnit)}
                        disabled={submitting}
                        data-testid="cp-release-btn"
                      >
                        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <X className="w-4 h-4 mr-2" />}
                        Release
                      </Button>
                    )}
                  </div>
                )}
                
                {selectedUnit.status === 'booked' && (
                  <p className="text-sm text-center text-muted-foreground">
                    This unit is booked and cannot be modified
                  </p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Block Dialog */}
      <Dialog open={isBlockDialogOpen} onOpenChange={setIsBlockDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Block Unit</DialogTitle>
            <DialogDescription>
              {selectedUnit && (canShowUnitNumber ? selectedUnit.unit_number : selectedUnit.configuration)} - Temporary hold
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Block Duration</Label>
              <Select 
                value={blockForm.block_hours.toString()} 
                onValueChange={(v) => setBlockForm({ ...blockForm, block_hours: parseInt(v) })}
              >
                <SelectTrigger data-testid="cp-block-duration">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 hours</SelectItem>
                  <SelectItem value="4">4 hours</SelectItem>
                  <SelectItem value="8">8 hours</SelectItem>
                  <SelectItem value="12">12 hours</SelectItem>
                  <SelectItem value="24">24 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Reason</Label>
              <Select 
                value={blockForm.reason} 
                onValueChange={(v) => setBlockForm({ ...blockForm, reason: v })}
              >
                <SelectTrigger data-testid="cp-block-reason">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer_interest">Customer Interest</SelectItem>
                  <SelectItem value="site_visit_scheduled">Site Visit Scheduled</SelectItem>
                  <SelectItem value="negotiation">Price Negotiation</SelectItem>
                  <SelectItem value="documentation">Documentation Review</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={blockForm.reason_detail}
                onChange={(e) => setBlockForm({ ...blockForm, reason_detail: e.target.value })}
                placeholder="Customer name, phone..."
                rows={2}
                data-testid="cp-block-notes"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBlockDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleBlock} 
              disabled={submitting}
              data-testid="cp-confirm-block"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Confirm Block
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChannelPartnerPortal;
